<template>
  <div class="stats-view">
    <BreadcrumbBar 
      :parentName="station?.name || 'Station'"
      currentName="Statistics"
      :backRoute="station ? `/station/${station.slug}` : '/'"
    />

    <div class="content" v-if="station">
      <!-- Tabs -->
      <div class="tabs-header">
        <button 
          :class="['tab-btn', { active: activeTab === 'overview' }]"
          @click="activeTab = 'overview'"
        >
          <AppIcon name="chart-bar" />Overview
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'search' }]"
          @click="activeTab = 'search'"
        >
          <AppIcon name="search" />Airplay Search
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'chartSync' }]"
          @click="activeTab = 'chartSync'; loadChartSync()"
        >
          <AppIcon name="music" />Chart Sync
        </button>
      </div>

      <!-- Overview Tab -->
      <div v-show="activeTab === 'overview'">
        <!-- Toolbar -->
        <div class="content-toolbar">
          <div class="toolbar-left">
            <router-link :to="`/compare?a=${slug}`" class="btn-compare-link"><AppIcon name="columns" />Compare</router-link>
          </div>
          <div class="toolbar-right">
            <div class="date-range-selector">
              <button 
                v-for="option in rangeOptions" 
                :key="option.days"
                :class="['btn-range', { active: selectedDays === option.days }]"
                @click="selectRange(option.days)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <AppIcon name="spinner" spin />
          <p>Loading statistics...</p>
        </div>

        <!-- No data state -->
        <div v-else-if="!stats || stats.totalCount === 0" class="empty-state">
          <AppIcon name="chart-bar" />
          <p>No statistics available for this period</p>
        </div>

        <!-- Stats content -->
        <template v-else>
          <!-- Summary cards -->
          <div class="summary-cards">
            <div class="summary-card">
              <div class="card-icon"><AppIcon name="music" /></div>
              <div class="card-content">
                <span class="card-value">{{ stats.totalCount.toLocaleString() }}</span>
                <span class="card-label">Total Plays</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="card-icon"><AppIcon name="layer-group" /></div>
              <div class="card-content">
                <span class="card-value">{{ Object.keys(stats.byCategory).length }}</span>
                <span class="card-label">{{ hasTypeCategories ? 'Categories' : 'Types' }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="card-icon"><AppIcon name="list" /></div>
              <div class="card-content">
                <span class="card-value">{{ stats.uniqueCount || stats.topPlayed.length }}</span>
                <span class="card-label">Unique Items</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="card-icon"><AppIcon name="calendar-day" /></div>
              <div class="card-content">
                <span class="card-value">{{ Object.keys(stats.byDate).length }}</span>
                <span class="card-label">Days with Data</span>
              </div>
            </div>
          </div>

          <!-- Charts row -->
          <div class="charts-row">
            <!-- Category breakdown -->
            <div class="chart-card">
              <h3>{{ hasTypeCategories ? 'Category' : 'Type' }} Breakdown</h3>
              <div class="chart-container">
                <canvas ref="categoryChart"></canvas>
              </div>
            </div>

              <!-- Hourly distribution -->
              <div class="chart-card">
                <h3>Hourly Distribution</h3>
                <div class="chart-container">
                  <canvas ref="hourlyChart"></canvas>
                </div>
              </div>
            </div>

            <!-- Daily plays chart -->
            <div class="chart-card full-width">
              <h3>Daily Plays</h3>
              <div class="chart-container chart-wide">
                <canvas ref="dailyChart"></canvas>
              </div>
            </div>

            <!-- Top played items -->
            <div class="chart-card full-width">
              <div class="section-header">
                <h3>Top Played Items</h3>
                <div class="top-played-actions">
                  <select
                    v-if="availableTypeFilters.length > 0"
                    v-model="topPlayedTypeFilter"
                    @change="onTopPlayedTypeFilterChange"
                    class="type-filter-select"
                  >
                    <option value="">All Categories</option>
                    <option v-for="cat in availableTypeFilters" :key="cat.value" :value="cat.value">
                      {{ cat.label }}
                    </option>
                  </select>
                  <button 
                    class="btn-export-csv" 
                    @click="exportTopPlayedToCsv"
                    :disabled="!stats || !topPlayedData.length"
                    title="Export to CSV"
                  >
                    <AppIcon name="download" />
                    <span>Export CSV</span>
                  </button>
                </div>
              </div>
              <div class="top-played-list" style="position: relative;">
                <div v-show="topPlayedLoading" class="top-played-loading">
                  <AppIcon name="spinner" spin :size="20" />
                </div>
                <div class="top-played-header">
                  <span class="col-rank">#</span>
                  <span class="col-info">Item</span>
                  <span class="col-category">{{ hasTypeCategories ? 'Category' : 'Type' }}</span>
                  <span class="col-count">Plays</span>
                </div>
                <div v-if="!topPlayedLoading && topPlayedData.length === 0" class="top-played-empty">
                  <AppIcon name="inbox" :size="28" />
                  <p>No tracks found for this category</p>
                </div>
                <div 
                  v-for="(item, index) in topPlayedData.slice(0, 100)" 
                  :key="index"
                  class="top-played-item"
                >
                  <span class="col-rank">{{ index + 1 }}</span>
                  <span class="col-info">
                    <span class="item-title">{{ item.title || 'Unknown' }}</span>
                    <span class="item-artist" v-if="item.artist">{{ item.artist }}</span>
                  </span>
                  <span class="col-category">
                    <span 
                      class="category-badge" 
                      :style="{ backgroundColor: getCategoryColor(item.category) }"
                    >
                      {{ item.category || item.type || 'UNKNOWN' }}
                    </span>
                  </span>
                  <span class="col-count">{{ item.count.toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

      <!-- Airplay Search Tab -->
      <div v-show="activeTab === 'search'" class="search-tab">
        <!-- Search toolbar -->
        <div class="search-toolbar">
          <div class="search-input-group">
            <AppIcon name="search" />
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search song, artist, or metadata..."
              @keyup.enter="performSearch"
            >
            <button class="btn-search" @click="performSearch" :disabled="!searchQuery.trim() || searchLoading">
              {{ searchLoading ? 'Searching...' : 'Search' }}
            </button>
          </div>
          <div class="search-filters">
            <div class="date-range-selector">
              <button 
                v-for="option in rangeOptions" 
                :key="option.days"
                :class="['btn-range', { active: searchDays === option.days }]"
                @click="setSearchRange(option.days)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Search results -->
        <div v-if="searchLoading" class="loading-state">
          <AppIcon name="spinner" spin />
          <p>Searching...</p>
        </div>

        <div v-else-if="searchPerformed && searchResults.length === 0" class="empty-state">
          <AppIcon name="search" />
          <p>No results found for "{{ lastSearchQuery }}"</p>
        </div>

        <div v-else-if="searchResults.length > 0" class="search-results">
          <!-- Results header -->
          <div class="results-header">
            <div class="results-count">
              <strong>{{ searchTotalCount.toLocaleString() }}</strong> plays found for "{{ lastSearchQuery }}"
            </div>
            <button class="btn-export-csv" @click="exportSearchResults" :disabled="searchExporting">
              <AppIcon name="download" />
              <span>{{ searchExporting ? 'Exporting...' : 'Export CSV' }}</span>
            </button>
          </div>

          <!-- Results table -->
          <div class="results-table">
            <div class="results-table-header">
              <span class="col-date">Date</span>
              <span class="col-time">Time</span>
              <span class="col-metadata">Metadata</span>
              <span class="col-type">Type</span>
            </div>
            <div 
              v-for="result in searchResults" 
              :key="result.id"
              class="results-table-row"
            >
              <span class="col-date">{{ formatDate(result.timestamp) }}</span>
              <span class="col-time">{{ formatTime(result.timestamp) }}</span>
              <span class="col-metadata">
                <span class="metadata-title">{{ result.title || result.raw }}</span>
                <span class="metadata-artist" v-if="result.artist">{{ result.artist }}</span>
              </span>
              <span class="col-type">
                <span class="category-badge" :style="{ backgroundColor: getCategoryColor(result.category || result.type) }">
                  {{ result.category || result.type || 'UNKNOWN' }}
                </span>
              </span>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="searchTotalPages > 1">
            <button 
              class="btn-page" 
              @click="goToPage(searchPage - 1)" 
              :disabled="searchPage === 1"
            >
              <AppIcon name="chevron-left" />
            </button>
            <span class="page-info">Page {{ searchPage }} of {{ searchTotalPages }}</span>
            <button 
              class="btn-page" 
              @click="goToPage(searchPage + 1)" 
              :disabled="searchPage === searchTotalPages"
            >
              <AppIcon name="chevron-right" />
            </button>
          </div>
        </div>

        <!-- Initial state -->
        <div v-else class="empty-state">
          <AppIcon name="search" />
          <p>Search for a song, artist, or any metadata text to see play history</p>
        </div>
      </div>

      <!-- Chart Sync Tab -->
      <div v-show="activeTab === 'chartSync'" class="chart-sync-tab">
        <!-- Chart Sources Management -->
        <div class="playlists-section">
          <div class="section-header">
            <h3>Chart Sources</h3>
            <span class="playlist-count">{{ chartSyncPlaylists.length }} / 5</span>
          </div>

          <!-- Add chart dropdown -->
          <div class="add-playlist-form">
            <select
              v-model="newPlaylistUrl"
              :disabled="addingPlaylist || chartSyncPlaylists.length >= 5"
              class="chart-select"
            >
              <option value="">Select a chart...</option>
              <optgroup label="🍎 Apple Music — Top 100">
                <option
                  v-for="chart in (availableCharts.apple || []).filter(c => !chartSyncPlaylists.some(p => p.id === c.id))"
                  :key="chart.id"
                  :value="chart.id"
                >{{ chart.name }}</option>
              </optgroup>
              <optgroup label="▶️ YouTube — Top Music">
                <option
                  v-for="chart in (availableCharts.youtube || []).filter(c => !chartSyncPlaylists.some(p => p.id === c.id))"
                  :key="chart.id"
                  :value="chart.id"
                >{{ chart.name }}</option>
              </optgroup>
            </select>
            <button
              class="btn-add-playlist"
              @click="addPlaylist"
              :disabled="!newPlaylistUrl || addingPlaylist || chartSyncPlaylists.length >= 5"
            >
              <AppIcon v-if="addingPlaylist" name="spinner" spin />
              <AppIcon v-else name="plus" />
              <span>Add</span>
            </button>
          </div>
          <p v-if="playlistError" class="playlist-error">{{ playlistError }}</p>

          <!-- Added charts list -->
          <div class="playlists-list" v-if="chartSyncPlaylists.length > 0">
            <div
              v-for="playlist in chartSyncPlaylists"
              :key="playlist.id"
              class="playlist-item"
            >
              <div class="playlist-image placeholder">
                <AppIcon :name="playlist.id && playlist.id.startsWith('yt-') ? 'youtube' : 'apple'" />
              </div>
              <div class="playlist-info">
                <span class="playlist-name">{{ playlist.name }}</span>
                <span class="playlist-tracks" v-if="playlist.trackCount">{{ playlist.trackCount }} tracks</span>
                <span class="playlist-tracks error" v-if="playlist.error">{{ playlist.error }}</span>
              </div>
              <button class="btn-remove-playlist" @click="removePlaylist(playlist.id)" title="Remove chart">
                <AppIcon name="times" />
              </button>
            </div>
          </div>
          <p v-else class="no-playlists">
            Add Apple Music or YouTube chart sources to compare against your station's airplay.
            Charts update daily with the latest streaming and view data.
          </p>
        </div>

        <!-- Comparison Results -->
        <div class="chart-sync-results" v-if="chartSyncPlaylists.length > 0">
          <div class="content-toolbar">
            <div class="toolbar-left">
              <span class="powered-by">
                <AppIcon name="apple" />Apple Music &amp; <AppIcon name="youtube" />YouTube
              </span>
            </div>
            <div class="toolbar-right">
              <div class="date-range-selector">
                <button 
                  v-for="option in rangeOptions" 
                  :key="option.days"
                  :class="['btn-range', { active: chartSyncDays === option.days }]"
                  @click="setChartSyncRange(option.days)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="chartSyncLoading" class="loading-state">
            <AppIcon name="spinner" spin />
            <p>Analyzing airplay vs charts...</p>
          </div>

          <!-- Results -->
          <template v-else-if="chartSyncData">
            <!-- Coverage summary -->
            <div class="coverage-summary">
              <div class="coverage-card main">
                <div class="coverage-value">{{ chartSyncData.coverage }}%</div>
                <div class="coverage-label">Coverage</div>
                <div class="coverage-detail">{{ chartSyncData.matchedCount }} of {{ chartSyncData.totalTracks }} tracks</div>
              </div>
              <div class="coverage-card">
                <div class="coverage-value">{{ chartSyncData.missingTotal ?? chartSyncData.missing?.length ?? 0 }}</div>
                <div class="coverage-label">Missing</div>
                <div class="coverage-detail">Not in rotation</div>
              </div>
              <div class="coverage-card">
                <div class="coverage-value">{{ chartSyncData.inSyncTotal ?? chartSyncData.inSync?.length ?? 0 }}</div>
                <div class="coverage-label">In Sync</div>
                <div class="coverage-detail">Playing & charting</div>
              </div>
            </div>

            <!-- Missing tracks -->
            <div class="tracks-section" v-if="chartSyncData.missing?.length > 0">
              <h4><AppIcon name="exclamation-triangle" />Missing from Rotation</h4>
              <p class="section-hint">These tracks are charting but not playing on your station</p>
              <div class="tracks-table">
                <div class="tracks-header">
                  <span class="col-position">#</span>
                  <span class="col-track">Track</span>
                  <span class="col-playlists">Charts</span>
                </div>
                <div 
                  v-for="item in chartSyncData.missing.slice(0, showAllMissing ? undefined : 10)" 
                  :key="item.track.id"
                  class="tracks-row missing"
                >
                  <span class="col-position">{{ item.chartPosition }}</span>
                  <span class="col-track">
                    <a :href="item.track.url" target="_blank" rel="noopener">
                      <strong>{{ item.track.name }}</strong>
                    </a>
                    <span class="track-artist">{{ item.track.artists.join(', ') }}</span>
                  </span>
                  <span class="col-playlists">
                    <span 
                      v-for="c in item.track.charts" 
                      :key="c" 
                      class="playlist-badge"
                    >{{ c }}</span>
                  </span>
                </div>
              </div>
              <button 
                v-if="chartSyncData.missing.length > 10" 
                class="btn-show-more"
                @click="showAllMissing = !showAllMissing"
              >
                {{ showAllMissing ? 'Show less' : `Show all ${chartSyncData.missingTotal ?? chartSyncData.missing.length} missing` }}
              </button>
            </div>

            <!-- In sync tracks -->
            <div class="tracks-section" v-if="chartSyncData.inSync?.length > 0">
              <h4><AppIcon name="check-circle" />In Sync</h4>
              <p class="section-hint">You're playing these charting tracks</p>
              <div class="tracks-table">
                <div class="tracks-header">
                  <span class="col-position">#</span>
                  <span class="col-track">Track</span>
                  <span class="col-plays">Your Plays</span>
                </div>
                <div 
                  v-for="item in chartSyncData.inSync.slice(0, showAllInSync ? undefined : 10)" 
                  :key="item.track.id"
                  class="tracks-row in-sync"
                >
                  <span class="col-position">{{ item.chartPosition }}</span>
                  <span class="col-track">
                    <a :href="item.track.url" target="_blank" rel="noopener">
                      <strong>{{ item.track.name }}</strong>
                    </a>
                    <span class="track-artist">{{ item.track.artists.join(', ') }}</span>
                    <span class="track-match">Matched: {{ item.stationMetadata }}</span>
                  </span>
                  <span class="col-plays">{{ item.playCount.toLocaleString() }}</span>
                </div>
              </div>
              <button 
                v-if="chartSyncData.inSync.length > 10" 
                class="btn-show-more"
                @click="showAllInSync = !showAllInSync"
              >
                {{ showAllInSync ? 'Show less' : `Show all ${chartSyncData.inSyncTotal ?? chartSyncData.inSync.length} in sync` }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div v-else class="loading-state">
      <p>Loading station...</p>
    </div>
  </div>
</template>

<script>
import { fetchMetadataStats, fetchStationBySlug, setStation, authFetch, fetchAvailableCharts, fetchChartSyncPlaylists, addChartSyncPlaylist, removeChartSyncPlaylist, fetchChartSync } from '../services/api'
import Chart from 'chart.js/auto'
import BreadcrumbBar from '../components/BreadcrumbBar.vue'

export default {
  name: 'StatsView',
  components: {
    BreadcrumbBar
  },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      station: null,
      stats: null,
      loading: false,
      selectedDays: 30,
      activeTab: 'overview',
      topPlayedTypeFilter: '',
      topPlayedLoading: false,
      topPlayedData: [],
      rangeOptions: [
        { days: 1, label: '24h' },
        { days: 7, label: '7 Days' },
        { days: 14, label: '14 Days' },
        { days: 30, label: '30 Days' }
      ],
      charts: {
        category: null,
        hourly: null,
        daily: null
      },
      // Search tab state
      searchQuery: '',
      lastSearchQuery: '',
      searchDays: 7,
      searchResults: [],
      searchTotalCount: 0,
      searchPage: 1,
      searchTotalPages: 0,
      searchLoading: false,
      searchPerformed: false,
      searchExporting: false,
      // Chart Sync tab state
      chartSyncPlaylists: [],
      availableCharts: { apple: [], youtube: [] },
      chartSyncData: null,
      chartSyncDays: 7,
      chartSyncLoading: false,
      newPlaylistUrl: '',
      addingPlaylist: false,
      playlistError: '',
      showAllMissing: false,
      showAllInSync: false
    }
  },
  async created() {
    await this.loadStation()
    await this.loadStats()
  },
  computed: {
    hasTypeCategories() {
      return this.stats?.typeCategories && this.stats.typeCategories.length > 0
    },
    availableTypeFilters() {
      if (!this.stats?.typeCategories?.length) return []
      return this.stats.typeCategories.map(cat => ({
        label: cat.label,
        // Pass all codes as comma-separated so backend can $in match
        value: (cat.codes || []).join(',') || cat.label
      }))
    },
    searchDateRange() {
      const now = Date.now()
      const from = now - (this.searchDays * 24 * 60 * 60 * 1000)
      return { from, to: now }
    }
  },
  beforeUnmount() {
    // Cleanup charts
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy()
    })
  },
  methods: {
    async loadStation() {
      this.station = await fetchStationBySlug(this.slug)
      if (this.station) {
        setStation(this.slug, this.station.liveAudioUrl)
      }
    },
    
    async loadStats(type = null) {
      this.loading = true
      try {
        const stats = await fetchMetadataStats(this.selectedDays, null, null, type || this.topPlayedTypeFilter || null)
        this.stats = stats
        this.topPlayedData = stats.topPlayed || []
        this.loading = false
        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (err) {
        console.error('Error loading stats:', err)
        this.loading = false
      }
    },

    async onTopPlayedTypeFilterChange() {
      this.topPlayedLoading = true
      try {
        const stats = await fetchMetadataStats(this.selectedDays, null, null, this.topPlayedTypeFilter || null)
        this.topPlayedData = stats.topPlayed || []
      } catch (err) {
        console.error('Error loading filtered top played:', err)
      } finally {
        this.topPlayedLoading = false
      }
    },

    async selectRange(days) {
      this.selectedDays = days
      await this.loadStats()
    },
    
    setSearchRange(days) {
      this.searchDays = days
      if (this.searchPerformed) {
        this.performSearch()
      }
    },
    
    async performSearch() {
      if (!this.searchQuery.trim()) return
      
      this.searchLoading = true
      this.searchPerformed = true
      this.lastSearchQuery = this.searchQuery.trim()
      this.searchPage = 1
      
      try {
        const { from, to } = this.searchDateRange
        const params = new URLSearchParams({
          q: this.lastSearchQuery,
          from: from.toString(),
          to: to.toString(),
          page: '1',
          limit: '50'
        })
        
        const res = await authFetch(`/api/s/${this.slug}/metadata/report?${params}`)
        if (res.ok) {
          const data = await res.json()
          this.searchResults = data.results
          this.searchTotalCount = data.totalCount
          this.searchPage = data.page
          this.searchTotalPages = data.totalPages
        }
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        this.searchLoading = false
      }
    },
    
    async goToPage(page) {
      if (page < 1 || page > this.searchTotalPages) return
      
      this.searchLoading = true
      
      try {
        const { from, to } = this.searchDateRange
        const params = new URLSearchParams({
          q: this.lastSearchQuery,
          from: from.toString(),
          to: to.toString(),
          page: page.toString(),
          limit: '50'
        })
        
        const res = await authFetch(`/api/s/${this.slug}/metadata/report?${params}`)
        if (res.ok) {
          const data = await res.json()
          this.searchResults = data.results
          this.searchTotalCount = data.totalCount
          this.searchPage = data.page
          this.searchTotalPages = data.totalPages
        }
      } catch (err) {
        console.error('Pagination error:', err)
      } finally {
        this.searchLoading = false
      }
    },
    
    async exportSearchResults() {
      this.searchExporting = true
      
      try {
        const { from, to } = this.searchDateRange
        const timezone = this.station?.timezone || 'America/New_York'
        const params = new URLSearchParams({
          q: this.lastSearchQuery,
          from: from.toString(),
          to: to.toString(),
          timezone
        })
        
        const res = await authFetch(`/api/s/${this.slug}/metadata/report/export?${params}`)
        if (res.ok) {
          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `airplay-report-${this.slug}-${new Date().toISOString().split('T')[0]}.csv`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        }
      } catch (err) {
        console.error('Export error:', err)
      } finally {
        this.searchExporting = false
      }
    },
    
    formatDate(timestamp) {
      const timezone = this.station?.timezone || 'America/New_York'
      return new Date(timestamp).toLocaleDateString('en-US', {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    
    formatTime(timestamp) {
      const timezone = this.station?.timezone || 'America/New_York'
      return new Date(timestamp).toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    },
    
    getCategoryColor(category) {
      if (!category) return this.stats?.defaultTypeColor || '#999999'
      
      const categories = this.stats?.typeCategories || []
      const found = categories.find(c => c.label === category)
      if (found?.color) return found.color
      
      // Generate a consistent color based on category name (for stations without typeCategories)
      // This ensures the same category always gets the same color
      const hash = category.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc)
      }, 0)
      
      // Use predefined palette of distinct colors
      const palette = [
        '#5D8AA8', '#E57373', '#81C784', '#FFB74D', '#64B5F6',
        '#BA68C8', '#4DB6AC', '#FF8A65', '#A1887F', '#90A4AE',
        '#F06292', '#AED581', '#7986CB', '#FFD54F', '#4DD0E1'
      ]
      
      return palette[Math.abs(hash) % palette.length]
    },
    
    renderCharts() {
      if (!this.stats) return
      
      this.renderCategoryChart()
      this.renderHourlyChart()
      this.renderDailyChart()
    },
    
    renderCategoryChart() {
      if (this.charts.category) this.charts.category.destroy()
      
      const canvas = this.$refs.categoryChart
      if (!canvas) return
      
      const categories = Object.entries(this.stats.byCategory)
        .sort((a, b) => b[1] - a[1])
      
      const labels = categories.map(([cat]) => cat)
      const data = categories.map(([, count]) => count)
      const colors = labels.map(cat => this.getCategoryColor(cat))
      const total = data.reduce((sum, val) => sum + val, 0)
      
      this.charts.category = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: colors,
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
              labels: {
                font: { size: 11 },
                padding: 12
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value = context.raw
                  const percentage = ((value / total) * 100).toFixed(1)
                  return `${context.label}: ${value} (${percentage}%)`
                }
              }
            }
          }
        }
      })
    },
    
    renderHourlyChart() {
      if (this.charts.hourly) this.charts.hourly.destroy()
      
      const canvas = this.$refs.hourlyChart
      if (!canvas) return
      
      const hours = Array.from({ length: 24 }, (_, i) => i)
      const data = hours.map(h => this.stats.byHour[h] || 0)
      
      this.charts.hourly = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: hours.map(h => `${h}:00`),
          datasets: [{
            label: 'Plays',
            data,
            backgroundColor: '#5D8AA8',
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 10 } }
            },
            y: {
              beginAtZero: true,
              grid: { color: '#f0f0f0' }
            }
          }
        }
      })
    },
    
    renderDailyChart() {
      if (this.charts.daily) this.charts.daily.destroy()
      
      const canvas = this.$refs.dailyChart
      if (!canvas) return
      
      const dates = Object.keys(this.stats.byDate).sort()
      const data = dates.map(d => this.stats.byDate[d])
      
      this.charts.daily = new Chart(canvas, {
        type: 'line',
        data: {
          labels: dates.map(d => {
            const date = new Date(d)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          }),
          datasets: [{
            label: 'Plays',
            data,
            borderColor: '#5D8AA8',
            backgroundColor: 'rgba(93, 138, 168, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: '#5D8AA8'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false }
          },
          scales: {
            x: {
              grid: { display: false }
            },
            y: {
              beginAtZero: true,
              grid: { color: '#f0f0f0' }
            }
          }
        }
      })
    },
    
    exportTopPlayedToCsv() {
      if (!this.topPlayedData.length) return
      
      const topPlayed = this.topPlayedData.slice(0, 100)
      
      // Build CSV content
      let csv = 'Rank,Title,Artist,Category,Plays\n'
      
      topPlayed.forEach((item, index) => {
        const title = (item.title || 'Unknown').replace(/"/g, '""')
        const artist = (item.artist || '').replace(/"/g, '""')
        const category = (item.category || item.type || 'UNKNOWN').replace(/"/g, '""')
        csv += `${index + 1},"${title}","${artist}","${category}",${item.count}\n`
      })
      
      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `top_played_${this.slug}_${this.selectedDays}days_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    
    // Chart Sync methods
    async loadChartSync() {
      // Load available Billboard charts and saved sources in parallel
      if (!this.availableCharts.apple?.length) {
        try {
          const res = await fetchAvailableCharts()
          this.availableCharts = res.charts || { apple: [], youtube: [] }
        } catch (err) {
          console.error('Error loading available charts:', err)
        }
      }
      if (this.chartSyncPlaylists.length === 0) {
        await this.loadPlaylists()
      }
      if (this.chartSyncPlaylists.length > 0 && !this.chartSyncData) {
        await this.loadChartSyncData()
      }
    },
    
    async loadPlaylists() {
      try {
        const response = await fetchChartSyncPlaylists()
        this.chartSyncPlaylists = response.playlists || []
      } catch (err) {
        console.error('Error loading playlists:', err)
      }
    },
    
    async addPlaylist() {
      if (!this.newPlaylistUrl || this.addingPlaylist) return
      
      this.addingPlaylist = true
      this.playlistError = ''
      
      try {
        const response = await addChartSyncPlaylist(this.newPlaylistUrl.trim())
        this.chartSyncPlaylists.push(response.playlist)
        this.newPlaylistUrl = ''
        await this.loadChartSyncData()
      } catch (err) {
        this.playlistError = err.message
      } finally {
        this.addingPlaylist = false
      }
    },
    
    async removePlaylist(playlistId) {
      try {
        await removeChartSyncPlaylist(playlistId)
        this.chartSyncPlaylists = this.chartSyncPlaylists.filter(p => p.id !== playlistId)
        // Reload chart sync data
        if (this.chartSyncPlaylists.length > 0) {
          await this.loadChartSyncData()
        } else {
          this.chartSyncData = null
        }
      } catch (err) {
        console.error('Error removing playlist:', err)
      }
    },
    
    async loadChartSyncData() {
      if (this.chartSyncPlaylists.length === 0) return
      
      this.chartSyncLoading = true
      this.showAllMissing = false
      this.showAllInSync = false
      
      try {
        this.chartSyncData = await fetchChartSync(this.chartSyncDays)
      } catch (err) {
        console.error('Error loading chart sync:', err)
      } finally {
        this.chartSyncLoading = false
      }
    },
    
    setChartSyncRange(days) {
      this.chartSyncDays = days
      this.loadChartSyncData()
    },
    
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.stats-view {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}

.content {
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Tabs */
.tabs-header {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: var(--white);
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
}

.tab-btn {
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-btn:hover {
  background: var(--gray-100);
}

.tab-btn.active {
  background: var(--gray-900);
  color: var(--white);
}

.tab-btn i {
  font-size: 12px;
}

.content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-compare-link {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-compare-link:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
  color: var(--gray-700);
}

.date-range-selector {
  display: flex;
  gap: 4px;
  background: var(--white);
  padding: 4px;
  border-radius: 6px;
}

.btn-range {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-range:hover {
  background: var(--gray-200);
}

.btn-range.active {
  background: #f5f5f5;
  color: var(--gray-900);
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--gray-500);
}

.loading-state i, .empty-state i {
  font-size: 32px;
  margin-bottom: 16px;
}

.empty-state i {
  color: var(--gray-300);
}

/* Summary cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background: var(--white);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: var(--shadow-sm);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f0f9ff;
  color: #0369a1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.card-content {
  display: flex;
  flex-direction: column;
}

.card-value {
  font-size: 24px;
  font-weight: 600;
  color: var(--gray-900);
  line-height: 1.2;
}

.card-label {
  font-size: 12px;
  color: var(--gray-500);
  margin-top: 2px;
}

/* Charts */
.charts-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-card {
  background: var(--white);
  border-radius: 8px;
  padding: 20px;
  box-shadow: var(--shadow-sm);
}

.chart-card.full-width {
  margin-bottom: 16px;
}

.chart-card h3 {
  font-size: 14px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 16px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
}

.top-played-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-filter-select {
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #333;
  cursor: pointer;
  outline: none;
}

.type-filter-select:hover {
  border-color: #aaa;
}

.btn-export-csv {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  background: var(--white);
  color: var(--gray-700);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.top-played-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-filter-select {
  padding: 6px 32px 6px 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-700);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

.type-filter-select:hover,
.type-filter-select:focus {
  border-color: var(--gray-400);
}

.btn-export-csv:hover:not(:disabled) {
  background: var(--gray-50);
  border-color: var(--gray-400);
}

.btn-export-csv:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chart-container {
  height: 250px;
}

.chart-container.chart-wide {
  height: 200px;
}

/* Top played list */
.top-played-list {
  height: 600px;
  overflow-y: auto;
}

.top-played-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--gray-400);
  font-size: 13px;
}

.top-played-empty p {
  margin: 0;
  color: var(--gray-400);
}

.top-played-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gray-50);
  z-index: 2;
  color: var(--gray-400);
  min-height: 80px;
}

.top-played-header {
  display: grid;
  grid-template-columns: 40px 1fr 120px 80px;
  gap: 12px;
  padding: 8px 12px;
  background: var(--gray-50);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.top-played-item {
  display: grid;
  grid-template-columns: 40px 1fr 120px 80px;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--gray-100);
  align-items: center;
}

.top-played-item:last-child {
  border-bottom: none;
}

.col-rank {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-400);
}

.col-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.item-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-artist {
  font-size: 12px;
  color: var(--gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-category {
  display: flex;
}

.category-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-count {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-700);
  text-align: right;
}

/* Search Tab */
.search-tab {
  background: var(--white);
  border-radius: 8px;
  padding: 24px;
}

.search-toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 300px;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 4px 12px;
}

.search-input-group svg {
  color: var(--gray-400);
  font-size: 14px;
}

.search-input-group input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 0;
  font-size: 14px;
  outline: none;
  color: var(--gray-900);
}

.search-input-group input::placeholder {
  color: var(--gray-400);
}

.btn-search {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  background: var(--gray-900);
  color: var(--white);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-search:hover:not(:disabled) {
  background: var(--gray-800);
}

.btn-search:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.search-filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Search Results */
.search-results {
  margin-top: 24px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.results-count {
  font-size: 14px;
  color: var(--gray-600);
}

.results-count strong {
  color: var(--gray-900);
}

.results-table {
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  overflow: hidden;
}

.results-table-header {
  display: grid;
  grid-template-columns: 100px 90px 1fr 100px;
  gap: 12px;
  padding: 12px 16px;
  background: var(--gray-50);
  font-size: 11px;
  font-weight: 600;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.results-table-row {
  display: grid;
  grid-template-columns: 100px 90px 1fr 100px;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid var(--gray-100);
  align-items: center;
}

.results-table-row:hover {
  background: var(--gray-50);
}

.col-date {
  font-size: 13px;
  color: var(--gray-700);
}

.col-time {
  font-size: 13px;
  color: var(--gray-500);
}

.col-metadata {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.metadata-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.metadata-artist {
  font-size: 12px;
  color: var(--gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-type {
  display: flex;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--gray-100);
}

.btn-page {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-page:hover:not(:disabled) {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.btn-page:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: var(--gray-600);
}

/* Responsive */
@media (max-width: 900px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .charts-row {
    grid-template-columns: 1fr;
  }
  
  .search-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input-group {
    min-width: unset;
  }
}

@media (max-width: 600px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }
  
  .top-played-header,
  .top-played-item {
    grid-template-columns: 30px 1fr 80px;
  }
  
  .col-category {
    display: none;
  }
  
  .results-table-header,
  .results-table-row {
    grid-template-columns: 80px 1fr 80px;
  }
  
  .col-time {
    display: none;
  }
}

/* ========== Chart Sync Tab ========== */
.chart-select {
  flex: 1;
  padding: 8px 36px 8px 12px;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 6px;
  background: var(--bg-primary, #fff);
  color: var(--text-primary, #111);
  font-size: 14px;
  height: 38px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
}

.chart-select:focus {
  outline: none;
  border-color: var(--color-primary, #3b82f6);
  box-shadow: 0 0 0 2px rgba(59,130,246,0.15);
}

.chart-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.playlist-tracks.error {
  color: var(--color-error, #e53e3e);
}

.chart-sync-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.playlists-section {
  background: var(--white);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid var(--gray-200);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-header h3 {
  font-size: 14px;
  font-weight: 600;
  color: var(--gray-800);
  margin: 0;
}

.playlist-count {
  font-size: 12px;
  color: var(--gray-500);
}

.add-playlist-form {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.add-playlist-form input {
  flex: 1;
  padding: 10px 14px;
  font-size: 13px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--gray-50);
}

.add-playlist-form input:focus {
  outline: none;
  border-color: var(--accent);
  background: var(--white);
}

.btn-add-playlist {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  background: var(--accent);
  color: var(--white);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.btn-add-playlist:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-add-playlist:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.playlist-error {
  color: #dc3545;
  font-size: 12px;
  margin: -8px 0 12px 0;
}

.playlists-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.playlist-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: var(--gray-50);
  border-radius: 6px;
}

.playlist-image {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
}

.playlist-image.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1DB954;
  color: white;
  font-size: 18px;
}

.playlist-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.playlist-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-800);
}

.playlist-tracks {
  font-size: 11px;
  color: var(--gray-500);
}

.btn-remove-playlist {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--gray-400);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-remove-playlist:hover {
  background: var(--gray-200);
  color: var(--gray-600);
}

.no-playlists {
  font-size: 13px;
  color: var(--gray-500);
  text-align: center;
  padding: 20px;
  background: var(--gray-50);
  border-radius: 6px;
  line-height: 1.5;
}

.chart-sync-results {
  background: var(--white);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid var(--gray-200);
}

.powered-by {
  font-size: 11px;
  color: #1DB954;
  display: flex;
  align-items: center;
  gap: 6px;
}

.powered-by i {
  font-size: 14px;
}

.coverage-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.coverage-card {
  background: var(--gray-50);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.coverage-card.main {
  background: linear-gradient(135deg, var(--accent), #5b9bd5);
  color: white;
}

.coverage-card.main .coverage-label,
.coverage-card.main .coverage-detail {
  color: rgba(255,255,255,0.85);
}

.coverage-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--gray-800);
}

.coverage-card.main .coverage-value {
  color: white;
}

.coverage-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

.coverage-detail {
  font-size: 11px;
  color: var(--gray-500);
  margin-top: 4px;
}

.tracks-section {
  margin-top: 24px;
}

.tracks-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: var(--gray-800);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tracks-section h4 i {
  font-size: 14px;
}

.tracks-section h4 .fa-exclamation-triangle {
  color: #f59e0b;
}

.tracks-section h4 .fa-check-circle {
  color: #10b981;
}

.section-hint {
  font-size: 12px;
  color: var(--gray-500);
  margin: 0 0 12px 0;
}

.tracks-table {
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  overflow: hidden;
}

.tracks-header {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  gap: 12px;
  padding: 10px 14px;
  background: var(--gray-50);
  font-size: 11px;
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.tracks-row {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  gap: 12px;
  padding: 12px 14px;
  border-top: 1px solid var(--gray-100);
  font-size: 13px;
  align-items: start;
}

.tracks-row.missing {
  background: #fef3f2;
}

.tracks-row.in-sync {
  background: #f0fdf4;
}

.col-position {
  font-weight: 600;
  color: var(--gray-500);
}

.col-track {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.col-track a {
  color: var(--gray-800);
  text-decoration: none;
}

.col-track a:hover {
  color: var(--accent);
}

.col-track strong {
  font-weight: 500;
}

.track-artist {
  font-size: 12px;
  color: var(--gray-500);
}

.track-match {
  font-size: 11px;
  color: var(--gray-400);
  font-style: italic;
}

.col-playlists {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 200px;
  justify-content: flex-end;
}

.playlist-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  white-space: normal;
  word-break: break-word;
  text-align: center;
}

.col-plays {
  font-weight: 500;
  color: var(--gray-700);
  text-align: right;
}

.btn-show-more {
  margin-top: 12px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-show-more:hover {
  background: var(--gray-50);
  border-color: var(--gray-400);
}

@media (max-width: 768px) {
  .coverage-summary {
    grid-template-columns: 1fr;
  }
  
  .tracks-header,
  .tracks-row {
    grid-template-columns: 40px 1fr;
  }
  
  .col-playlists,
  .col-plays {
    display: none;
  }
}
</style>
